/**
 * Story 30.2 (ruling O15) — the one builder for every bounce to sign-in.
 *
 * The route guards (`ProtectedRoute`, `RequireOwner`, `RequireSuperuser`) and
 * the session-expiry bounce (`clearAuthAndRedirectToLogin`) all send people to
 * `/login?next=<path+search>` so sign-in can return them where they were going
 * (a tapped push to `/tasks?taskId=…`, a bookmarked chat). The WHOLE query is
 * kept — `/chat` drops `advisoryId` without `agent`. `next` is encoded once
 * here and decoded once by react-router's `URLSearchParams.get`.
 *
 * No `next` for `/` (nothing to return to) or for any target `safeReturnPath`
 * would refuse anyway (the sign-in pages themselves, anything not an in-app
 * path) — those get a bare `/login`. One exception: a bounce FROM `/login`
 * that already carries a `next` `safeReturnPath` accepts unchanged keeps it —
 * a wrong-password 401 on a signed-out tab goes through the session-expiry
 * bounce, and must not throw the destination away. Explicit and peer logout
 * never come here: they stay bare `/login` on purpose.
 */
import { safeReturnPath } from './safeRedirectPath';

export interface LoginPathLocation {
  pathname: string;
  search?: string;
}

export function loginPathFor(location: LoginPathLocation): string {
  if (location.pathname === '/login') {
    const next = new URLSearchParams(location.search ?? '').get('next');
    if (next !== null && safeReturnPath(next) === next) {
      return `/login?next=${encodeURIComponent(next)}`;
    }
    return '/login';
  }
  const target = location.pathname + (location.search ?? '');
  if (location.pathname === '/' || safeReturnPath(target) !== target) return '/login';
  return `/login?next=${encodeURIComponent(target)}`;
}
